'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { CheckCircle2, Zap, Crown, Sparkles, Star, ArrowRight, Infinity,  } from 'lucide-react';

/* ─── Types ─────────────────────────────────────────────────── */
type BillingCycle = 'monthly' | 'quarterly' | 'semi' | 'annual' | 'lifetime';

interface PricingTranslations {
  eyebrow: string;
  title: string;
  sub: string;
  cycles: {
    monthly: string;
    quarterly: string;
    semi: string;
    annual: string;
    lifetime: string;
    saveBadge40: string;
    vipBadge: string;
  };
  tiers: {
    free: TierTranslation;
    pro: TierTranslation;
    proMax: TierTranslation;
    lifetime: LifetimeTierTranslation;
  };
  lifetimeCard: {
    title: string;
    subtitle: string;
    proLabel: string;
    proMaxLabel: string;
    oneTime: string;
    features: string[];
    cta: string;
  };
}

interface TierTranslation {
  name: string;
  tagline: string;
  features: string[];
  cta: string;
  badge?: string;
  mostPopular?: string;
}

interface LifetimeTierTranslation {
  name: string;
  tagline: string;
}

/* ─── Pricing Data ──────────────────────────────────────────── */
const PRICING = {
  free: { monthly: 0, quarterly: 0, semi: 0, annual: 0 },
  pro: {
    monthly: { price: 4.99, billed: null, perMonth: 4.99 },
    quarterly: { price: 12.99, billed: 'quarterly', perMonth: 4.33 },
    semi: { price: 21.99, billed: 'semi-annually', perMonth: 3.66 },
    annual: { price: 35.99, billed: 'annually', perMonth: 2.99 },
  },
  proMax: {
    monthly: { price: 12.99, billed: null, perMonth: 12.99 },
    quarterly: { price: 32.99, billed: 'quarterly', perMonth: 10.99 },
    semi: { price: 54.99, billed: 'semi-annually', perMonth: 9.16 },
    annual: { price: 89.99, billed: 'annually', perMonth: 7.49 },
  },
};

/* ─── Translations ──────────────────────────────────────────── */
const pricingTranslations: Record<'en' | 'ar' | 'fr', PricingTranslations> = {
  en: {
    eyebrow: 'Transparent Pricing',
    title: 'Invest in your financial future',
    sub: 'Choose the plan that fits your wealth-building journey. Every paid plan includes a 14-day free trial — no credit card required.',
    cycles: {
      monthly: 'Monthly',
      quarterly: 'Quarterly',
      semi: 'Semi-Annual',
      annual: 'Annual',
      lifetime: 'Lifetime',
      saveBadge40: 'SAVE 40%',
      vipBadge: 'VIP EXCLUSIVE',
    },
    tiers: {
      free: {
        name: 'Starter',
        tagline: 'Basic personal expense & balance tracking',
        features: [
          '1 Financial Account Link',
          'Basic Monthly Budgeting',
          'Manual Expense Logging',
          'Basic Dashboard Analytics',
          'Standard Community Support',
        ],
        cta: 'Get Started',
      },
      pro: {
        name: 'Financial Freedom',
        tagline: 'Automated wealth building and analytics',
        features: [
          'Unlimited Bank & Crypto Account Sync',
          'Advanced AI Wealth Insights & Expense Categorization',
          'Real-time Multi-Currency & Asset Tracking',
          'Custom Monthly & Yearly Financial Goals',
          'Export Data (CSV, PDF Reports)',
          'Priority Support',
        ],
        cta: 'Start 14-Day Free Trial',
        badge: 'Pro',
      },
      proMax: {
        name: 'Wealth Mastery',
        tagline: 'For high-net-worth individuals & family offices',
        features: [
          'Everything in Pro',
          'Family & Multi-User Sharing (Up to 4 Profiles)',
          'AI Tax Optimization & Financial Scenario Simulator',
          'Personalized Investment Portfolio Risk Analysis',
          'Automated Debt Payoff & Passive Income Planner',
          '1-on-1 Financial Advisor Consultation (Bi-annually)',
          '24/7 VIP Concierge Support',
        ],
        cta: 'Unlock Wealth Mastery',
        badge: 'Pro Max',
        mostPopular: 'Most Popular',
      },
      lifetime: {
        name: 'Legacy VIP',
        tagline: 'For long-term investors who hate subscription fees',
      },
    },
    lifetimeCard: {
      title: 'Legacy VIP',
      subtitle: 'Pay once. Own it forever. No recurring charges — ever.',
      proLabel: 'Pro Lifetime',
      proMaxLabel: 'Pro Max Lifetime',
      oneTime: 'one-time',
      features: [
        'Pay once, access forever — no recurring charges',
        'All future features and updates included automatically',
        'Exclusive Gold-badged VIP Profile Status',
        'Direct Alpha/Beta feature access before public release',
      ],
      cta: 'Claim Lifetime Access',
    },
  },
  ar: {
    eyebrow: 'أسعار شفافة',
    title: 'استثمر في مستقبلك المالي',
    sub: 'اختر الخطة التي تناسب رحلة بناء ثروتك. كل خطة مدفوعة تتضمن تجربة مجانية 14 يوماً — بدون بطاقة ائتمان.',
    cycles: {
      monthly: 'شهري',
      quarterly: 'ربع سنوي',
      semi: 'نصف سنوي',
      annual: 'سنوي',
      lifetime: 'مدى الحياة',
      saveBadge40: 'وفر 40%',
      vipBadge: 'VIP حصري',
    },
    tiers: {
      free: {
        name: 'المبتدئ',
        tagline: 'تتبع أساسي للمصروفات والرصيد الشخصي',
        features: [
          'ربط حساب مالي واحد',
          'ميزانية شهرية أساسية',
          'تسجيل يدوي للمصروفات',
          'تحليلات لوحة تحكم أساسية',
          'دعم مجتمعي قياسي',
        ],
        cta: 'ابدأ مجاناً',
      },
      pro: {
        name: 'الحرية المالية',
        tagline: 'بناء ثروة آلي وتحليلات متقدمة',
        features: [
          'مزامنة غير محدودة للبنوك والعملات الرقمية',
          'رؤى ثروة AI متقدمة وتصنيف المصروفات',
          'تتبع متعدد العملات والأصول في الوقت الفعلي',
          'أهداف مالية شهرية وسنوية مخصصة',
          'تصدير البيانات (CSV، تقارير PDF)',
          'دعم أولوية',
        ],
        cta: 'ابدأ تجربة 14 يوماً',
        badge: 'Pro',
      },
      proMax: {
        name: 'إتقان الثروة',
        tagline: 'لأصحاب الثروات العالية ومكاتب العائلة',
        features: [
          'كل مميزات Pro',
          'مشاركة عائلية ومتعددة المستخدمين (حتى 4 ملفات)',
          'تحسين ضريبي بالذكاء الاصطناعي ومحاكي السيناريوهات',
          'تحليل مخاطر محفظة الاستثمار الشخصية',
          'مخطط سداد الديون التلقائي والدخل السلبي',
          'استشارة مستشار مالي 1-على-1 (مرتين سنوياً)',
          'دعم VIP على مدار الساعة',
        ],
        cta: 'افتح إتقان الثروة',
        badge: 'Pro Max',
        mostPopular: 'الأكثر شعبية',
      },
      lifetime: {
        name: 'VIP الإرث',
        tagline: 'للمستثمرين على المدى الطويل الذين يكرهون رسوم الاشتراك',
      },
    },
    lifetimeCard: {
      title: 'VIP الإرث',
      subtitle: 'ادفع مرة واحدة. امتلكه للأبد. لا رسوم متكررة — أبداً.',
      proLabel: 'Pro مدى الحياة',
      proMaxLabel: 'Pro Max مدى الحياة',
      oneTime: 'دفعة واحدة',
      features: [
        'ادفع مرة واحدة، وصول للأبد — بدون رسوم متكررة',
        'جميع الميزات والتحديثات المستقبلية مدرجة تلقائياً',
        'حالة ملف VIP حصري بشارة ذهبية',
        'وصول مباشر لميزات Alpha/Beta قبل الإصدار العام',
      ],
      cta: 'احصل على وصول مدى الحياة',
    },
  },
  fr: {
    eyebrow: 'Tarification transparente',
    title: 'Investissez dans votre avenir financier',
    sub: 'Choisissez le plan qui correspond à votre parcours de construction de richesse. Chaque plan payant inclut un essai gratuit de 14 jours — sans carte bancaire.',
    cycles: {
      monthly: 'Mensuel',
      quarterly: 'Trimestriel',
      semi: 'Semestriel',
      annual: 'Annuel',
      lifetime: 'À vie',
      saveBadge40: 'ÉCONOMISEZ 40%',
      vipBadge: 'VIP EXCLUSIF',
    },
    tiers: {
      free: {
        name: 'Débutant',
        tagline: 'Suivi basique des dépenses et du solde personnel',
        features: [
          '1 lien de compte financier',
          'Budget mensuel de base',
          'Enregistrement manuel des dépenses',
          'Analyses de tableau de bord de base',
          'Support communautaire standard',
        ],
        cta: 'Commencer',
      },
      pro: {
        name: 'Liberté Financière',
        tagline: 'Construction de richesse automatisée et analyses',
        features: [
          'Synchronisation illimitée banque & crypto',
          'Insights IA avancés & catégorisation des dépenses',
          'Suivi multi-devises & actifs en temps réel',
          'Objectifs financiers mensuels & annuels personnalisés',
          'Export des données (CSV, rapports PDF)',
          'Support prioritaire',
        ],
        cta: 'Essai gratuit 14 jours',
        badge: 'Pro',
      },
      proMax: {
        name: 'Maîtrise du Patrimoine',
        tagline: 'Pour les grandes fortunes et les family offices',
        features: [
          'Tout ce qui est dans Pro',
          'Partage familial multi-utilisateurs (jusqu\'à 4 profils)',
          'Optimisation fiscale IA & simulateur de scénarios',
          'Analyse des risques de portefeuille personnalisée',
          'Planificateur de remboursement de dettes automatisé',
          'Consultation conseiller financier 1-sur-1 (bi-annuel)',
          'Support VIP 24/7',
        ],
        cta: 'Débloquer la Maîtrise',
        badge: 'Pro Max',
        mostPopular: 'Le plus populaire',
      },
      lifetime: {
        name: 'VIP Héritage',
        tagline: 'Pour les investisseurs à long terme qui détestent les abonnements',
      },
    },
    lifetimeCard: {
      title: 'VIP Héritage',
      subtitle: 'Payez une fois. Possédez-le pour toujours. Aucun frais récurrent — jamais.',
      proLabel: 'Pro À vie',
      proMaxLabel: 'Pro Max À vie',
      oneTime: 'paiement unique',
      features: [
        'Payez une fois, accès pour toujours — sans frais récurrents',
        'Toutes les futures fonctionnalités et mises à jour incluses automatiquement',
        'Statut de profil VIP exclusif avec badge doré',
        'Accès direct aux fonctionnalités Alpha/Beta avant la sortie publique',
      ],
      cta: 'Obtenir l\'accès à vie',
    },
  },
};

/* ─── Helpers ───────────────────────────────────────────────── */
function formatPrice(val: number) {
  return val % 1 === 0 ? `$${val}` : `$${val.toFixed(2)}`;
}

function getCycleLabel(cycle: BillingCycle, t: PricingTranslations) {
  const map: Record<BillingCycle, string> = {
    monthly: t.cycles.monthly,
    quarterly: t.cycles.quarterly,
    semi: t.cycles.semi,
    annual: t.cycles.annual,
    lifetime: t.cycles.lifetime,
  };
  return map[cycle];
}

/* ─── Component ─────────────────────────────────────────────── */
interface PricingSectionProps {
  lang: 'en' | 'ar';
  isRtl: boolean;
}

export default function PricingSection({ lang, isRtl }: PricingSectionProps) {
  const [cycle, setCycle] = useState<BillingCycle>('monthly');
  const [animKey, setAnimKey] = useState(0);
  const t = pricingTranslations[lang];

  const handleCycleChange = (c: BillingCycle) => {
    setCycle(c);
    setAnimKey((k) => k + 1);
  };

  const isLifetime = cycle === 'lifetime';

  const proData = isLifetime ? null : PRICING.pro[cycle as keyof typeof PRICING.pro];
  const proMaxData = isLifetime ? null : PRICING.proMax[cycle as keyof typeof PRICING.proMax];

  const cycles: BillingCycle[] = ['monthly', 'quarterly', 'semi', 'annual', 'lifetime'];

  const getCheckoutUrl = (plan: 'free' | 'pro' | 'pro_max' | 'pro_lifetime' | 'pro_max_lifetime', selectedCycle?: BillingCycle) => {
    if (plan === 'free') return '/register';
    // Lifetime plans are not yet connected to Stripe — redirect to register for now
    if (plan === 'pro_lifetime' || plan === 'pro_max_lifetime') return '/register?plan=lifetime';
    // Monthly subscription plans → server-side Stripe Checkout
    const c = selectedCycle ?? cycle;
    if (c === 'monthly') {
      // Only monthly prices are connected to Stripe recurring price IDs
      const stripeMap: Record<string, string> = {
        pro: '/api/stripe/checkout?plan=pro',
        pro_max: '/api/stripe/checkout?plan=pro_max',
      };
      if (stripeMap[plan]) return stripeMap[plan];
    }
    // Non-monthly cycles: redirect to register with plan info (Stripe prices not yet created for these)
    return `/register?plan=${plan}&cycle=${c}`;
  };

  const isStripeCheckout = (plan: 'free' | 'pro' | 'pro_max' | 'pro_lifetime' | 'pro_max_lifetime', selectedCycle?: BillingCycle) => {
    const c = selectedCycle ?? cycle;
    return (plan === 'pro' || plan === 'pro_max') && c === 'monthly';
  };

  return (
    <section id="pricing" className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20 lg:py-24">
      {/* Header */}
      <div className="text-center mb-10 sm:mb-12">
        <p className="text-xs font-bold text-amber-800 dark:text-primary uppercase tracking-widest mb-3">{t.eyebrow}</p>
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground mb-4">{t.title}</h2>
        <p className="text-muted-foreground max-w-xl mx-auto text-sm leading-relaxed">{t.sub}</p>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex justify-center mb-10 sm:mb-12">
        <div className="relative flex items-center gap-1 p-1.5 rounded-2xl border border-border bg-card/60 backdrop-blur-sm shadow-lg overflow-x-auto max-w-full">
          {cycles.map((c) => {
            const isActive = cycle === c;
            const isAnnual = c === 'annual';
            const isLT = c === 'lifetime';
            return (
              <button
                key={c}
                onClick={() => handleCycleChange(c)}
                className={`relative flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-4 py-2 rounded-xl text-[11px] sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap flex-shrink-0 ${
                  isActive
                    ? isLT
                      ? 'bg-gradient-to-r from-primary to-[#F3E5AB] text-primary-foreground shadow-lg shadow-primary/30'
                      : 'bg-foreground text-background shadow-lg shadow-foreground/20' :'text-muted-foreground hover:text-foreground hover:bg-muted/60'
                }`}
              >
                {isLT && <Infinity size={11} className={isActive ? 'text-primary-foreground' : 'text-primary dark:text-primary text-amber-700'} />}
                {getCycleLabel(c, t)}
                {isAnnual && (
                  <span
                    className={`hidden sm:inline ml-1 text-[9px] font-black px-1.5 py-0.5 rounded-full tracking-wider ${
                      isActive
                        ? 'bg-foreground/20 text-background'
                        : 'bg-primary/20 text-amber-800 dark:text-primary animate-pulse'
                    }`}
                  >
                    {t.cycles.saveBadge40}
                  </span>
                )}
                {isLT && !isActive && (
                  <span className="hidden sm:inline ml-1 text-[9px] font-black px-1.5 py-0.5 rounded-full tracking-wider bg-primary/20 text-amber-800 dark:text-primary">
                    VIP
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Main 3-Column Grid */}
      {!isLifetime && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mb-6 sm:mb-8" key={animKey}>
          {/* FREE */}
          <div className="relative rounded-3xl p-6 border border-border bg-card flex flex-col pricing-card-enter">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Free</p>
            </div>
            <p className="text-lg font-bold text-card-foreground mb-1">{t.tiers.free.name}</p>
            <p className="text-xs text-muted-foreground mb-5 leading-relaxed">{t.tiers.free.tagline}</p>

            <div className="flex items-baseline gap-1 mb-6">
              <span className="text-4xl font-black text-card-foreground">$0</span>
              <span className="text-sm text-muted-foreground">/ forever</span>
            </div>

            <ul className="space-y-2.5 mb-6 flex-1">
              {t.tiers.free.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 size={15} className="text-positive flex-shrink-0 mt-0.5" />
                  <span className="text-card-foreground">{f}</span>
                </li>
              ))}
            </ul>

            <Link
              href={getCheckoutUrl('free')}
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 border border-border bg-muted/30 text-card-foreground hover:bg-muted/60 hover:border-border/80 backdrop-blur-sm"
            >
              {t.tiers.free.cta} {!isRtl && <ArrowRight size={14} />}
            </Link>
          </div>

          {/* PRO */}
          <div className="relative rounded-3xl p-6 border border-primary/30 bg-card flex flex-col shadow-xl shadow-primary/10 pricing-card-enter" style={{ animationDelay: '0.08s' }}>
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-primary/8 to-transparent pointer-events-none" />

            <div className="flex items-center gap-2 mb-1">
              <Zap size={14} className="text-primary" />
              <p className="text-xs font-bold text-primary uppercase tracking-widest">Pro</p>
            </div>
            <p className="text-lg font-bold text-card-foreground mb-1">{t.tiers.pro.name}</p>
            <p className="text-xs text-muted-foreground mb-5 leading-relaxed">{t.tiers.pro.tagline}</p>

            <div className="flex flex-col mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black text-card-foreground">
                  {formatPrice((proData as typeof PRICING.pro.monthly).price)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {cycle === 'monthly' ? '/mo' : ' total'}
                </span>
              </div>
              {cycle !== 'monthly' && (
                <p className="text-xs text-primary mt-1 font-semibold">
                  ≈ {formatPrice((proData as typeof PRICING.pro.quarterly).perMonth)}/mo · billed {(proData as typeof PRICING.pro.quarterly).billed}
                </p>
              )}
            </div>

            <ul className="space-y-2.5 mb-6 flex-1">
              {t.tiers.pro.features.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2 size={15} className="text-positive flex-shrink-0 mt-0.5" />
                  <span className="text-card-foreground">{f}</span>
                </li>
              ))}
            </ul>

            {isStripeCheckout('pro', cycle) ? (
              <a
                href={getCheckoutUrl('pro', cycle)}
                className="relative flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 overflow-hidden bg-muted border border-primary/60 text-primary hover:border-primary hover:bg-primary/10 shadow-lg shadow-primary/10"
              >
                <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-primary/10 to-transparent opacity-0 hover:opacity-100 transition-opacity pointer-events-none" />
                {t.tiers.pro.cta} {!isRtl && <ArrowRight size={14} />}
              </a>
            ) : (
              <Link
                href={getCheckoutUrl('pro', cycle)}
                className="relative flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm transition-all active:scale-95 overflow-hidden bg-muted border border-primary/60 text-primary hover:border-primary hover:bg-primary/10 shadow-lg shadow-primary/10"
              >
                <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-primary/10 to-transparent opacity-0 hover:opacity-100 transition-opacity pointer-events-none" />
                {t.tiers.pro.cta} {!isRtl && <ArrowRight size={14} />}
              </Link>
            )}
          </div>

          {/* PRO MAX — theme-adaptive premium card */}
          <div
            className="relative rounded-3xl p-6 flex flex-col pricing-card-enter bg-card-premium border border-primary/45"
            style={{
              animationDelay: '0.16s',
              boxShadow: '0 0 40px rgba(201,162,39,0.12), 0 20px 60px rgba(0,0,0,0.15)',
              zIndex: 10,
            }}
          >
            {/* Gold ambient glow */}
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-b from-primary/10 to-transparent pointer-events-none" />
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-primary/8 blur-3xl pointer-events-none" />

            {/* Most Popular ribbon */}
            <div
              className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-[11px] font-black whitespace-nowrap tracking-wider"
              style={{ background: 'linear-gradient(90deg, #C9A227, #F3E5AB, #C9A227)', color: '#0A0F1D' }}
            >
              ✦ {t.tiers.proMax.mostPopular}
            </div>

            <div className="flex items-center gap-2 mb-1 mt-2">
              <Crown size={14} className="text-primary" />
              <p className="text-xs font-bold uppercase tracking-widest text-primary">Pro Max</p>
            </div>
            <p className="text-lg font-bold text-card-premium-foreground mb-1">{t.tiers.proMax.name}</p>
            <p className="text-xs text-muted-foreground mb-5 leading-relaxed">{t.tiers.proMax.tagline}</p>

            <div className="flex flex-col mb-6">
              <div className="flex items-baseline gap-1">
                <span className="text-4xl font-black text-gold-text">
                  {formatPrice((proMaxData as typeof PRICING.proMax.monthly).price)}
                </span>
                <span className="text-sm text-muted-foreground">
                  {cycle === 'monthly' ? '/mo' : ' total'}
                </span>
              </div>
              {cycle !== 'monthly' && (
                <p className="text-xs mt-1 font-semibold text-primary">
                  ≈ {formatPrice((proMaxData as typeof PRICING.proMax.quarterly).perMonth)}/mo · billed {(proMaxData as typeof PRICING.proMax.quarterly).billed}
                </p>
              )}
            </div>

            <ul className="space-y-2.5 mb-6 flex-1">
              {t.tiers.proMax.features.map((f, i) => (
                <li key={f} className="flex items-start gap-2.5 text-sm">
                  <CheckCircle2
                    size={15}
                    className={`flex-shrink-0 mt-0.5 ${i === 0 ? 'text-primary' : 'text-positive'}`}
                  />
                  <span className="text-card-premium-foreground">{f}</span>
                </li>
              ))}
            </ul>

            {isStripeCheckout('pro_max', cycle) ? (
              <a
                href={getCheckoutUrl('pro_max', cycle)}
                className="relative flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm transition-all active:scale-95 overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #C9A227 0%, #F3E5AB 50%, #C9A227 100%)',
                  color: '#0A0F1D',
                  boxShadow: '0 4px 20px rgba(201,162,39,0.4)',
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity pointer-events-none" />
                <Sparkles size={14} />
                {t.tiers.proMax.cta}
              </a>
            ) : (
              <Link
                href={getCheckoutUrl('pro_max', cycle)}
                className="relative flex items-center justify-center gap-2 w-full py-3 rounded-xl font-bold text-sm transition-all active:scale-95 overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #C9A227 0%, #F3E5AB 50%, #C9A227 100%)',
                  color: '#0A0F1D',
                  boxShadow: '0 4px 20px rgba(201,162,39,0.4)',
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 hover:opacity-100 transition-opacity pointer-events-none" />
                <Sparkles size={14} />
                {t.tiers.proMax.cta}
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Lifetime Full-Width Luxury Card */}
      {isLifetime && (
        <div
          className="relative rounded-3xl overflow-hidden mb-8 pricing-card-enter bg-card-premium border border-primary/50"
          style={{
            boxShadow: '0 0 80px rgba(201,162,39,0.12), 0 30px 80px rgba(0,0,0,0.2)',
          }}
        >
          {/* Ambient glows */}
          <div className="absolute top-0 left-1/4 w-96 h-64 rounded-full bg-primary/8 blur-[80px] pointer-events-none" />
          <div className="absolute bottom-0 right-1/4 w-80 h-48 rounded-full bg-primary/6 blur-[60px] pointer-events-none" />

          {/* VIP Badge */}
          <div
            className="absolute top-5 right-5 sm:right-8 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-black tracking-widest"
            style={{ background: 'linear-gradient(90deg, #C9A227, #F3E5AB)', color: '#0A0F1D' }}
          >
            <Star size={10} className="fill-current" />
            VIP EXCLUSIVE
          </div>

          <div className="relative z-10 p-8 sm:p-12">
            <div className="flex items-center gap-2 mb-2">
              <Infinity size={18} className="text-primary" />
              <p className="text-xs font-black uppercase tracking-widest text-primary">
                {t.lifetimeCard.title}
              </p>
            </div>
            <h3 className="text-2xl sm:text-3xl font-black text-card-premium-foreground mb-2">{t.tiers.lifetime.name}</h3>
            <p className="text-sm text-muted-foreground mb-10 max-w-lg">{t.lifetimeCard.subtitle}</p>

            {/* Two pricing options */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-10">
              {/* Pro Lifetime */}
              <div
                className="rounded-2xl p-6 border border-primary/30 bg-primary/5"
              >
                <div className="flex items-center gap-2 mb-3">
                  <Zap size={14} className="text-primary" />
                  <p className="text-sm font-bold text-primary">{t.lifetimeCard.proLabel}</p>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-4xl font-black text-gold-text">$119</span>
                  <span className="text-xs text-muted-foreground">{t.lifetimeCard.oneTime}</span>
                </div>
                <p className="text-xs text-muted-foreground">All Pro features, forever</p>
              </div>

              {/* Pro Max Lifetime */}
              <div
                className="rounded-2xl p-6 relative overflow-hidden border border-primary/50"
                style={{
                  background: 'linear-gradient(135deg, rgba(201,162,39,0.12) 0%, rgba(201,162,39,0.05) 100%)',
                  boxShadow: '0 0 30px rgba(201,162,39,0.08)',
                }}
              >
                <div
                  className="absolute top-2 right-2 text-[9px] font-black px-2 py-0.5 rounded-full"
                  style={{ background: 'linear-gradient(90deg, #C9A227, #F3E5AB)', color: '#0A0F1D' }}
                >
                  BEST VALUE
                </div>
                <div className="flex items-center gap-2 mb-3">
                  <Crown size={14} className="text-primary" />
                  <p className="text-sm font-bold text-primary">{t.lifetimeCard.proMaxLabel}</p>
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-4xl font-black text-gold-text">$249</span>
                  <span className="text-xs text-muted-foreground">{t.lifetimeCard.oneTime}</span>
                </div>
                <p className="text-xs text-muted-foreground">All Pro Max features, forever</p>
              </div>
            </div>

            {/* Features */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {t.lifetimeCard.features.map((f) => (
                <div key={f} className="flex items-start gap-2.5">
                  <CheckCircle2 size={15} className="text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-card-premium-foreground">{f}</span>
                </div>
              ))}
            </div>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
              <Link
                href={getCheckoutUrl('pro_max_lifetime', 'lifetime')}
                className="relative flex items-center gap-2.5 px-8 py-4 rounded-xl font-black text-sm transition-all active:scale-95 overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #C9A227 0%, #F3E5AB 40%, #D4AF37 70%, #C9A227 100%)',
                  color: '#0A0F1D',
                  boxShadow: '0 6px 30px rgba(201,162,39,0.5)',
                  letterSpacing: '0.02em',
                }}
              >
                <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent opacity-0 hover:opacity-100 transition-opacity pointer-events-none" />
                <Sparkles size={16} />
                {t.lifetimeCard.cta}
                {!isRtl && <ArrowRight size={14} />}
              </Link>
              <p className="text-xs text-muted-foreground">
                ✓ Secure one-time payment &nbsp;·&nbsp; ✓ Instant access &nbsp;·&nbsp; ✓ No subscriptions
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lifetime teaser below main grid */}
      {!isLifetime && (
        <div
          className="relative rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 cursor-pointer group border border-primary/30 bg-primary/5"
          onClick={() => handleCycleChange('lifetime')}
        >
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #C9A227, #F3E5AB)', boxShadow: '0 4px 15px rgba(201,162,39,0.3)' }}
            >
              <Infinity size={18} style={{ color: '#0A0F1D' }} />
            </div>
            <div>
              <p className="text-sm font-bold text-foreground">
                Legacy VIP — Lifetime Access
              </p>
              <p className="text-xs text-muted-foreground">
                Pro from <span className="text-primary font-bold">$119</span> · Pro Max from <span className="text-primary font-bold">$249</span> · Pay once, own forever
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-xl transition-all text-primary border border-primary/40">
            <Sparkles size={12} />
            View Lifetime Plans
            <ArrowRight size={12} />
          </div>
        </div>
      )}
    </section>
  );
}
